import type { Comment, CommentSelectedText, CommentSource, CommentSubmission } from "../../types"
import { ulid } from "../../utils/ulid"
import type { CodeStore } from "../store"

export class CommentManager {
    constructor(private store: CodeStore) {}

    async addComment(taskId: string, source: CommentSource, content: string, selectedText: CommentSelectedText): Promise<string> {
        const commentId = ulid()
        const now = new Date().toISOString()
        const comment: Comment = {
            id: commentId,
            content,
            source,
            selectedText,
            author: this.store.currentUser,
            createdAt: now,
            updatedAt: now,
        }
        await this.updateTaskComments(taskId, (comments) => [...comments, comment])
        return commentId
    }

    async removeComment(taskId: string, commentId: string): Promise<void> {
        await this.updateTaskComments(taskId, (comments) => comments.filter((comment) => comment.id !== commentId))
    }

    async editComment(taskId: string, commentId: string, newContent: string): Promise<void> {
        const updatedAt = new Date().toISOString()
        await this.updateTaskComments(taskId, (comments) =>
            comments.map((comment) => (comment.id === commentId ? { ...comment, content: newContent, updatedAt } : comment))
        )
    }

    async markCommentsSubmitted({
        taskId,
        commentIds,
        submission,
    }: {
        taskId: string
        commentIds: readonly string[]
        submission: CommentSubmission
    }): Promise<void> {
        const submittedIds = new Set(commentIds)
        if (!this.getComments(taskId).some((comment) => submittedIds.has(comment.id) && !comment.submission)) return
        await this.updateTaskComments(taskId, (comments) =>
            comments.map((comment) => (submittedIds.has(comment.id) && !comment.submission ? { ...comment, submission } : comment))
        )
    }

    async restoreCommentsForTurn(taskId: string, turnId: string): Promise<void> {
        if (!this.getComments(taskId).some((comment) => comment.submission?.turnId === turnId)) return
        await this.updateTaskComments(taskId, (comments) =>
            comments.map((comment) => {
                if (comment.submission?.turnId !== turnId) return comment
                const { submission: _submission, ...pendingComment } = comment
                return pendingComment
            })
        )
    }

    async rebindCommentsForTurn({
        taskId,
        previousTurnId,
        submission,
    }: {
        taskId: string
        previousTurnId: string
        submission: CommentSubmission
    }): Promise<void> {
        if (!this.getComments(taskId).some((comment) => comment.submission?.turnId === previousTurnId)) return
        await this.updateTaskComments(taskId, (comments) =>
            comments.map((comment) => (comment.submission?.turnId === previousTurnId ? { ...comment, submission } : comment))
        )
    }

    getIncludedCommentIds(taskId: string): Set<string> {
        return new Set(
            this.getComments(taskId)
                .filter((comment) => comment.submission !== undefined)
                .map((comment) => comment.id)
        )
    }

    getUnsubmittedComments(taskId: string): Comment[] {
        const includedIds = this.getIncludedCommentIds(taskId)
        return this.getComments(taskId).filter((c) => !includedIds.has(c.id))
    }

    getPendingCommentCount(taskId: string): number {
        return this.getUnsubmittedComments(taskId).length
    }

    private getComments(taskId: string): Comment[] {
        const record = this.store.productState?.tasks.get(taskId)
        return record?.comments ?? []
    }

    private async updateTaskComments(taskId: string, apply: (comments: Comment[]) => Comment[]): Promise<void> {
        const productState = this.store.productState
        const record = productState?.tasks.get(taskId)
        if (!productState || !record) throw new Error(`Task ${taskId} not found`)

        const comments = apply(this.getComments(taskId).map((comment) => ({ ...comment })))
        await productState.putTask({
            ...record,
            comments,
            updatedAt: new Date().toISOString(),
        })
    }
}
